import { getNextPageRouteDetails } from "@tm/insurance/api";
import { navigationActions } from "@tm/insurance/navigation";
import { PARTNER, RESPONSE_STATUS } from "@tm/isomorphic-utils";
import { ClientError, setSessionStorageItem } from "@tm/utility";

/** Shape used to build `userSearchIntent` for recent-search → listing (desktop + Mweb). */
export type RecentSearchNavigateItem = {
  countryCode?: string[];
  travelDuration?: { startDate?: number; endDate?: number };
  travellerNum?: number;
  planType?: string;
  tripOption?: string;
  type?: string;
};

/**
 * Builds the request body for {@link getNextPageRouteDetails} from a recent search row.
 * Mirrors {@link RecentSearches} (desktop) `handleRecentSearchClick`.
 */
export function buildRecentSearchNextPageRequest(
  item: RecentSearchNavigateItem,
  itineraryId?: string
): Record<string, unknown> {
  const id =
    itineraryId ??
    (typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("itineraryId") || ""
      : "");

  return {
    query: {
      itineraryId: id,
      source: "standalone_regular",
      type: item?.type,
    },
    partner: PARTNER.MMT,
    userSearchIntent: {
      country: item?.countryCode,
      departureDateTime: item?.travelDuration?.startDate,
      arrivalDateTime: item?.travelDuration?.endDate,
      numberOfTravellers: item?.travellerNum,
      segment:
        item?.tripOption === "MULTITRIP" ? item?.tripOption : item?.planType,
      tripOption: item?.tripOption,
      planType: item?.planType,
    },
  };
}

/**
 * Resolves next route from CB and navigates (same as common-landing desktop / TravelInsuranceCard).
 */
export async function navigateRecentSearchNextPage(
  requestData: Record<string, unknown>,
  isFederated?: boolean
): Promise<void> {
  if (typeof window === "undefined") return;
  try {
    const response = await getNextPageRouteDetails(requestData as any);
    const { responseStatus, nextPageRouteConfig } = response;
    if (responseStatus === RESPONSE_STATUS.SUCCESS) {
      setSessionStorageItem(
        "ins_listing_landing_source",
        "COMMON_LANDING",
        true
      );
      if (nextPageRouteConfig?.type === "external_link") {
        window.open(nextPageRouteConfig?.route as string, "_blank");
      } else {
        let route = nextPageRouteConfig?.route as string;
        if (isFederated) {
          window.location.href = route;
        } else {
          if (
            route?.startsWith("/insurance") &&
            !window.location.pathname.includes("/travel-insurance")
          ) {
            route = route.replace("/insurance", "");
          }
          navigationActions.navigateToPageByRouteName(route);
        }
      }
    }
  } catch (err: unknown) {
    console.error("NEXT PAGE ROUTE FAILURE ERROR ----", { err });
    const axiosErr = err as { response?: { data?: { error?: unknown } } };
    const { error } = axiosErr?.response?.data || {};
    throw new ClientError(error as any);
  }
}
